import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Building2, Users, UserCog, DollarSign, FileBox, TrendingUp, ShieldAlert,
  Trash2, KeyRound, UserCheck, Search, Save, AlertTriangle, Pencil, Plus, Info, Copy,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ROLE_LABELS } from "@/lib/nav";

function StatCard({ label, value, hint, icon: Icon, accent }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{label}</div>
            <div className="text-3xl font-bold tracking-tight mt-2">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1.5">{hint}</div>}
          </div>
          <div className={`size-10 rounded-md grid place-items-center ${accent || "bg-primary/10 text-primary"}`}>
            <Icon className="size-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ConfirmDelete({ open, onClose, onConfirm, title, description, dangerLabel = "DELETE" }) {
  const [text, setText] = useState("");
  useEffect(() => { if (!open) setText(""); }, [open]);  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-5" /> {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{description}</p>
        <div className="space-y-2">
          <Label className="text-xs">Type <code className="mono bg-muted px-1.5 py-0.5 rounded">{dangerLabel}</code> to confirm</Label>
          <Input value={text} onChange={(e) => setText(e.target.value)} className="mono" data-testid="confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={text !== dangerLabel} onClick={() => { onConfirm(); onClose(); }} data-testid="confirm-delete-btn">
            Permanently delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function describePendingDelete(pending) {
  if (!pending) return "";
  if (pending.type === "tenant") {
    return `This will permanently delete the tenant "${pending.name}" and ALL of its patients, appointments, records, prescriptions, invoices and files. There is no undo.`;
  }
  if (pending.type === "patient") {
    return `Deletes ${pending.name} along with all their appointments, records, prescriptions, lab requests, invoices and login.`;
  }
  return `Permanently delete user ${pending.name}.`;
}

const EDITABLE_ROLES = ["doctor", "admin", "receptionist", "nurse", "pharmacist", "lab_tech", "patient"];

function EditTenantDialog({ tenant, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (tenant) {
      setForm({
        name: tenant.name || "", specialty: tenant.specialty || "",
        bio: tenant.bio || "", services: (tenant.services || []).join(", "),
        location: tenant.location || "", phone: tenant.phone || "",
        photo_url: tenant.photo_url || "", accept_bookings: tenant.accept_bookings !== false,
      });
    }
  }, [tenant]);
  if (!tenant) return null;
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/tenants/${tenant.id}`, {
        ...form,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Tenant updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  return (
    <Dialog open={!!tenant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit tenant — /d/{tenant.slug}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Practice name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-tenant-name" /></div>
            <div className="space-y-1.5"><Label>Specialty</Label>
              <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Bio</Label>
            <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Services (comma-separated)</Label>
            <Input value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Photo URL</Label>
            <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} /></div>
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div>
              <Label>Accept bookings</Label>
              <div className="text-xs text-muted-foreground">Public form on/off.</div>
            </div>
            <Switch checked={form.accept_bookings} onCheckedChange={(v) => setForm({ ...form, accept_bookings: v })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="edit-tenant-save">Save changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ target, tenants, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (target) {
      setForm({
        name: target.name || "", email: target.email || "",
        phone: target.phone || "", role: target.role || "doctor",
        specialty: target.specialty || "", tenant_id: target.tenant_id || target.tenant?.id || "",
      });
    }
  }, [target]);
  if (!target) return null;
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${target.id}`, form);
      toast.success("User updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit user — {target.email}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" /></div>
            <div className="space-y-1.5"><Label>Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{EDITABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {form.role === "doctor" && (
            <div className="space-y-1.5"><Label>Specialty</Label>
              <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
          )}
          <div className="space-y-1.5"><Label>Tenant (move user)</Label>
            <Select value={form.tenant_id || "_none"} onValueChange={(v) => setForm({ ...form, tenant_id: v === "_none" ? null : v })}>
              <SelectTrigger><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— No tenant —</SelectItem>
                {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (/d/{t.slug})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="edit-user-save">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateUserDialog({ open, tenants, onClose, onCreated }) {
  const EMPTY = { name: "", email: "", password: "", phone: "", role: "doctor", specialty: "", tenant_id: "" };
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { if (!open) setForm(EMPTY); /* eslint-disable-next-line */ }, [open]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add user to any tenant</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3" data-testid="admin-create-user-form">
          <div className="space-y-1.5"><Label>Tenant *</Label>
            <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
              <SelectTrigger data-testid="admin-create-user-tenant"><SelectValue placeholder="Pick a tenant" /></SelectTrigger>
              <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (/d/{t.slug})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Password *</Label>
              <Input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{EDITABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            {form.role === "doctor" && (
              <div className="space-y-1.5"><Label>Specialty</Label>
                <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!form.tenant_id} data-testid="admin-create-user-save">Create user</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditPatientDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (target) {
      setForm({
        name: target.name || "", phone: target.phone || "",
        email: target.email || "", gender: target.gender || "",
        dob: target.dob || "", blood_type: target.blood_type || "",
        address: target.address || "", emergency_contact: target.emergency_contact || "",
        insurance_provider: target.insurance_provider || "",
        insurance_number: target.insurance_number || "",
        allergies: target.allergies || "", conditions: target.conditions || "",
        notes: target.notes || "",
      });
    }
  }, [target]);
  if (!target) return null;
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/patients/${target.id}`, form);
      toast.success("Patient updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit patient — {target.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Name *</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-patient-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Gender</Label>
            <Input value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>DOB</Label>
            <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Blood type</Label>
            <Input value={form.blood_type} onChange={(e) => setForm({ ...form, blood_type: e.target.value })} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Emergency contact</Label>
            <Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Insurance provider</Label>
            <Input value={form.insurance_provider} onChange={(e) => setForm({ ...form, insurance_provider: e.target.value })} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Allergies</Label>
            <Textarea value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Conditions</Label>
            <Textarea value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="edit-patient-save">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreatePatientDialog({ open, tenants, onClose, onCreated }) {
  const EMPTY = { tenant_id: "", name: "", email: "", password: "", phone: "", gender: "", dob: "", blood_type: "", address: "", emergency_contact: "", allergies: "", conditions: "", notes: "" };
  const [form, setForm] = useState(EMPTY);
  const [creds, setCreds] = useState(null);
  useEffect(() => { if (!open) { setForm(EMPTY); setCreds(null); } /* eslint-disable-next-line */ }, [open]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/patients", form);
      setCreds(data.credentials);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        {creds ? (
          <>
            <DialogHeader><DialogTitle>Patient account created</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-muted flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Email</span>
                <span className="mono font-medium">{creds.email}</span>
              </div>
              <div className="p-3 rounded-md bg-muted flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Password</span>
                <span className="mono font-medium">{creds.password}</span>
              </div>
              <Button variant="outline" className="gap-2" onClick={() => {
                navigator.clipboard.writeText(`Email: ${creds.email}\nPassword: ${creds.password}`);
                toast.success("Copied");
              }}><Copy className="size-4" /> Copy</Button>
            </div>
            <DialogFooter>
              <Button onClick={onClose} data-testid="admin-create-patient-done">Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle>Add patient to any tenant</DialogTitle></DialogHeader>
            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-create-patient-form">
              <div className="space-y-1.5 sm:col-span-2"><Label>Tenant *</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger data-testid="admin-create-patient-tenant"><SelectValue placeholder="Pick a tenant" /></SelectTrigger>
                  <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (/d/{t.slug})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email *</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Password (auto if blank)</Label>
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="auto-generated" /></div>
              <div className="space-y-1.5"><Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Gender</Label>
                <Input value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>DOB</Label>
                <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Blood type</Label>
                <Input value={form.blood_type} onChange={(e) => setForm({ ...form, blood_type: e.target.value })} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Allergies</Label>
                <Textarea value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Conditions</Label>
                <Textarea value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} /></div>
              <DialogFooter className="sm:col-span-2">
                <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={!form.tenant_id || !form.name || !form.email} data-testid="admin-create-patient-save">Create patient & login</Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SuperAdminPage() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [userQ, setUserQ] = useState("");
  const [patQ, setPatQ] = useState("");
  const [settings, setSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // {type, id, name}
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [editTenant, setEditTenant] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [createPatientOpen, setCreatePatientOpen] = useState(false);

  const loadAll = useCallback(async () => {
    const [o, t, u, p, s] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/tenants"),
      api.get("/admin/all-users"),
      api.get("/admin/all-patients"),
      api.get("/admin/settings"),
    ]);
    setOverview(o.data); setTenants(t.data); setUsers(u.data); setPatients(p.data); setSettings(s.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredUsers = users.filter((u) =>
    !userQ ||
    u.name?.toLowerCase().includes(userQ.toLowerCase()) ||
    u.email?.toLowerCase().includes(userQ.toLowerCase()) ||
    u.role?.toLowerCase().includes(userQ.toLowerCase())
  );
  const filteredPatients = patients.filter((p) =>
    !patQ ||
    p.name?.toLowerCase().includes(patQ.toLowerCase()) ||
    p.phone?.toLowerCase().includes(patQ.toLowerCase()) ||
    p.email?.toLowerCase().includes(patQ.toLowerCase())
  );

  const doDelete = async () => {
    if (!pendingDelete) return;
    const { type, id } = pendingDelete;
    try {
      if (type === "tenant") await api.delete(`/admin/tenants/${id}`);
      if (type === "user") await api.delete(`/admin/users/${id}`);
      if (type === "patient") await api.delete(`/admin/patients/${id}`);
      toast.success("Permanently deleted");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const impersonate = async (u) => {
    try {
      await api.post(`/admin/impersonate/${u.id}`);
      await refresh();
      toast.success(`Signed in as ${u.name}`);
      nav("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const resetPassword = async () => {
    try {
      await api.post(`/admin/users/${resetUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetUser.email}`);
      setResetUser(null); setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        app_name: settings.app_name,
        accent_color: settings.accent_color || null,
        primary_color: settings.primary_color || null,
        default_logo_url: settings.default_logo_url || null,
        support_email: settings.support_email || null,
        allow_new_tenants: settings.allow_new_tenants !== false,
      };
      await api.put("/admin/settings", payload);
      toast.success("Platform settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSavingSettings(false); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="super-admin-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold flex items-center gap-1.5">
            <ShieldAlert className="size-3.5 text-accent" /> Platform owner
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Control Center</h1>
          <p className="text-sm text-muted-foreground mt-1">Total platform overview · destructive actions allowed.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Tenants" value={overview?.tenants ?? "—"} icon={Building2} />
        <StatCard label="Doctors" value={overview?.doctors ?? "—"} icon={UserCheck} accent="bg-accent/15 text-accent" />
        <StatCard label="Patients" value={overview?.patients ?? "—"} icon={Users} accent="bg-[hsl(var(--info))]/15 text-[hsl(var(--info))]" />
        <StatCard label="Appts (mo)" value={overview?.appointments_month ?? "—"} hint={`${overview?.appointments ?? 0} total`} icon={TrendingUp} />
        <StatCard label="Revenue" value={`$${(overview?.revenue_total ?? 0).toLocaleString()}`} hint={`$${overview?.revenue_outstanding?.toLocaleString() ?? 0} due`} icon={DollarSign} accent="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" />
        <StatCard label="Files" value={overview?.files ?? "—"} hint={`${overview?.files_size_mb ?? 0} MB`} icon={FileBox} />
      </div>

      <Card className="bg-accent/10 border-accent/30">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="size-5 text-accent shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong className="block">Full platform control.</strong>
            <span className="text-muted-foreground">
              Edit / delete any tenant, user, or patient directly from this page. For appointments, EMR, prescriptions, labs, invoices and files of a specific practice — click{" "}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-background border border-border mono text-[10px]"><UserCheck className="size-3" /> Impersonate</span>{" "}
              on a doctor to enter their workspace with complete CRUD access.
            </span>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="tenants">
        <TabsList>
          <TabsTrigger value="tenants" data-testid="admin-tab-tenants">Tenants ({tenants.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="patients" data-testid="admin-tab-patients">Patients ({patients.length})</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="admin-tab-revenue">Revenue</TabsTrigger>
          <TabsTrigger value="growth" data-testid="admin-tab-growth">Growth</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Branding</TabsTrigger>
        </TabsList>

        {/* TENANTS */}
        <TabsContent value="tenants">
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-end mb-3">
                <Button size="sm" className="gap-1.5" onClick={() => nav("/app/tenants")} data-testid="admin-new-tenant-btn">
                  <Plus className="size-3.5" /> New tenant
                </Button>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-3 px-2">Practice</th><th>Slug</th><th>Specialty</th><th>Doctor</th><th>Bookings</th><th className="px-2 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {tenants.map((t) => (
                    <tr key={t.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                      <td className="py-3 px-2 font-medium">{t.name}</td>
                      <td className="mono text-xs text-muted-foreground">/d/{t.slug}</td>
                      <td className="text-xs">{t.specialty || "—"}</td>
                      <td className="mono text-[11px] text-muted-foreground">{t.doctor_id?.slice(0, 8)}...</td>
                      <td><Badge variant="outline">{t.accept_bookings ? "Open" : "Closed"}</Badge></td>
                      <td className="px-2 py-3 text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditTenant(t)} title="Edit tenant" data-testid={`edit-tenant-${t.slug}`}>
                          <Pencil className="size-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive"
                          onClick={() => setPendingDelete({ type: "tenant", id: t.id, name: t.name })}
                          data-testid={`del-tenant-${t.slug}`}>
                          <Trash2 className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {tenants.length === 0 && <tr><td colSpan="6" className="py-10 text-center text-muted-foreground">No tenants yet.</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2 mb-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={userQ} onChange={(e) => setUserQ(e.target.value)} placeholder="Search by name / email / role…" className="pl-9" data-testid="admin-user-search" />
                </div>
                <Button className="gap-1.5" onClick={() => setCreateUserOpen(true)} data-testid="admin-new-user-btn">
                  <Plus className="size-4" /> Add user
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="py-3 pr-3">User</th><th>Role</th><th>Tenant</th><th className="text-right">Actions</th>
                  </tr></thead>
                  <tbody>
                    {filteredUsers.slice(0, 200).map((u) => (
                      <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2.5">
                            <Avatar className="size-7"><AvatarFallback className="bg-primary text-primary-foreground text-[10px]">{u.name?.slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
                            <div>
                              <div className="font-medium">{u.name}</div>
                              <div className="text-[10px] text-muted-foreground">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td><Badge variant="outline" className="text-[10px]">{ROLE_LABELS[u.role] || u.role}</Badge></td>
                        <td className="text-xs text-muted-foreground">{u.tenant?.name || "—"}</td>
                        <td className="text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => impersonate(u)} title="Sign in as" data-testid={`impersonate-${u.id}`}>
                            <UserCheck className="size-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditUser(u)} title="Edit" data-testid={`edit-user-${u.id}`}>
                            <Pencil className="size-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setResetUser(u); setNewPassword(""); }} title="Reset password" data-testid={`reset-${u.id}`}>
                            <KeyRound className="size-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive" disabled={u.role === "super_admin"}
                            onClick={() => setPendingDelete({ type: "user", id: u.id, name: u.email })} data-testid={`del-user-${u.id}`}>
                            <Trash2 className="size-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && <tr><td colSpan="4" className="py-10 text-center text-muted-foreground">No users.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PATIENTS */}
        <TabsContent value="patients">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2 mb-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={patQ} onChange={(e) => setPatQ(e.target.value)} placeholder="Search across all tenants…" className="pl-9" data-testid="admin-pat-search" />
                </div>
                <Button className="gap-1.5" onClick={() => setCreatePatientOpen(true)} data-testid="admin-new-patient-btn">
                  <Plus className="size-4" /> Add patient
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="py-3 pr-3">Patient</th><th>Tenant</th><th>Phone</th><th>DOB</th><th className="text-right">Actions</th>
                  </tr></thead>
                  <tbody>
                    {filteredPatients.slice(0, 200).map((p) => (
                      <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                        <td className="py-2 pr-3 font-medium">{p.name}</td>
                        <td className="text-xs text-muted-foreground">{p.tenant?.name || "—"}</td>
                        <td className="mono text-[11px]">{p.phone || "—"}</td>
                        <td className="text-xs">{p.dob || "—"}</td>
                        <td className="text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditPatient(p)} title="Edit" data-testid={`edit-patient-${p.id}`}>
                            <Pencil className="size-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive"
                            onClick={() => setPendingDelete({ type: "patient", id: p.id, name: p.name })}
                            data-testid={`del-patient-${p.id}`}>
                            <Trash2 className="size-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {filteredPatients.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-muted-foreground">No patients.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REVENUE */}
        <TabsContent value="revenue">
          <Card>
            <CardHeader><CardTitle>Revenue by tenant</CardTitle></CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={overview?.revenue_by_tenant?.map((r) => ({ name: r.tenant.name, paid: r.paid, invoices: r.invoices })) || []} margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Bar dataKey="paid" radius={[6, 6, 0, 0]}>
                      {(overview?.revenue_by_tenant || []).map((r, i) => <Cell key={r.tenant?.id || i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GROWTH */}
        <TabsContent value="growth">
          <Card>
            <CardHeader><CardTitle>Patient growth (last 6 months)</CardTitle></CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer>
                  <AreaChart data={overview?.growth_chart || []}>
                    <defs>
                      <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Area type="monotone" dataKey="patients" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#g)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SETTINGS / BRANDING */}
        <TabsContent value="settings">
          <Card>
            <CardHeader><CardTitle>Platform branding</CardTitle></CardHeader>
            <CardContent className="space-y-4 max-w-xl">
              <div className="space-y-1.5"><Label>App name</Label>
                <Input value={settings.app_name || ""} onChange={(e) => setSettings({ ...settings, app_name: e.target.value })} data-testid="settings-app-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Primary color (HSL)</Label>
                  <Input value={settings.primary_color || ""} onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })} placeholder="165 40% 25%" /></div>
                <div className="space-y-1.5"><Label>Accent color (HSL)</Label>
                  <Input value={settings.accent_color || ""} onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })} placeholder="25 60% 55%" /></div>
              </div>
              <div className="space-y-1.5"><Label>Default logo URL (new tenants)</Label>
                <Input value={settings.default_logo_url || ""} onChange={(e) => setSettings({ ...settings, default_logo_url: e.target.value })} placeholder="https://..." /></div>
              <div className="space-y-1.5"><Label>Support email</Label>
                <Input type="email" value={settings.support_email || ""} onChange={(e) => setSettings({ ...settings, support_email: e.target.value })} /></div>
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div>
                  <Label>Allow new tenant creation</Label>
                  <div className="text-xs text-muted-foreground mt-0.5">When off, the New Tenant flow is locked.</div>
                </div>
                <Switch checked={settings.allow_new_tenants !== false} onCheckedChange={(v) => setSettings({ ...settings, allow_new_tenants: v })} />
              </div>
              <Button onClick={saveSettings} disabled={savingSettings} className="gap-2" data-testid="save-settings-btn">
                <Save className="size-4" /> {savingSettings ? "Saving..." : "Save settings"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmDelete
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={doDelete}
        title={`Delete ${pendingDelete?.type}?`}
        description={describePendingDelete(pendingDelete)}
      />

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password — {resetUser?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>New password *</Label>
              <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mono" data-testid="reset-pwd-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)}>Cancel</Button>
            <Button onClick={resetPassword} disabled={!newPassword} data-testid="reset-pwd-confirm">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditTenantDialog
        tenant={editTenant}
        onClose={() => setEditTenant(null)}
        onSaved={() => { setEditTenant(null); loadAll(); }}
      />
      <EditUserDialog
        target={editUser}
        tenants={tenants}
        onClose={() => setEditUser(null)}
        onSaved={() => { setEditUser(null); loadAll(); }}
      />
      <CreateUserDialog
        open={createUserOpen}
        tenants={tenants}
        onClose={() => setCreateUserOpen(false)}
        onCreated={() => { setCreateUserOpen(false); loadAll(); }}
      />
      <EditPatientDialog
        target={editPatient}
        onClose={() => setEditPatient(null)}
        onSaved={() => { setEditPatient(null); loadAll(); }}
      />
      <CreatePatientDialog
        open={createPatientOpen}
        tenants={tenants}
        onClose={() => setCreatePatientOpen(false)}
        onCreated={() => loadAll()}
      />
    </div>
  );
}
